import {
  CUSTOM_DESIGN_ASPECT_RATIO,
  CUSTOM_DESIGN_ASPECT_RATIO_TOLERANCE,
  CUSTOM_DESIGN_REFERENCE_GUIDE_FILENAME,
  CUSTOM_DESIGN_REFERENCE_GUIDE_URL,
} from './constants';

/**
 * Downloads the reference guide. The `download` attribute is ignored for
 * cross-origin URLs, so the file is fetched and saved from a blob instead.
 * Falls back to opening it in a new tab if the fetch is blocked.
 */
export const downloadReferenceGuide = async () => {
  try {
    const response = await fetch(CUSTOM_DESIGN_REFERENCE_GUIDE_URL);

    if (!response.ok) {
      throw new Error(`Unexpected response: ${response.status}`);
    }

    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = CUSTOM_DESIGN_REFERENCE_GUIDE_FILENAME;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    window.open(CUSTOM_DESIGN_REFERENCE_GUIDE_URL, '_blank');
  }
};

/**
 * Returns whether the given dimensions match the expected 8.5" x 11"
 * portrait aspect ratio, within tolerance.
 *
 * @param {Number} width The image width.
 * @param {Number} height The image height.
 * @returns {boolean}
 */
export const isValidDesignAspectRatio = (width, height) => {
  if (!width || !height) {
    return false;
  }

  return (
    Math.abs(width / height - CUSTOM_DESIGN_ASPECT_RATIO) <=
    CUSTOM_DESIGN_ASPECT_RATIO_TOLERANCE
  );
};

/**
 * Returns the validation error for a custom design file, if any.
 * Dimension validation happens separately, after the image is readable.
 *
 * @param {File} file The picked file.
 * @returns {String|null}
 */
export const getDesignFileError = file => {
  if (!file || file.size === 0) {
    return 'Please provide a PNG image.';
  }

  if (file.type !== 'image/png') {
    return 'Please upload your design as a .png file.';
  }

  return null;
};

/**
 * Validates a custom design file, including its aspect ratio.
 *
 * @param {File} file The picked file.
 * @returns {Promise<String|null>} The validation error, if any.
 */
export const validateDesignFile = file => {
  const fileError = getDesignFileError(file);

  if (fileError) {
    return Promise.resolve(fileError);
  }

  return new Promise(resolve => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(
        isValidDesignAspectRatio(image.naturalWidth, image.naturalHeight)
          ? null
          : 'Your design must have an 8.5" x 11" portrait aspect ratio.',
      );
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve('We could not read that image. Please try another .png file.');
    };
    image.src = objectUrl;
  });
};
